import {
  CheckCircleOutlined,
  DownOutlined,
  IdcardOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, message } from 'antd';
import { isAxiosError } from 'axios';
import i18next from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as API from '../api';
import colors from '../theme/colors';
import OpenClassSessionParticipantTable, {
  participantStates,
} from '../components/OpenClassSessionParticipantTable';
import SafeLifeSpin from '../components/SafeLifeSpin';
import { useAppBar } from '../context/AppBar';
import { useAppContainer } from '../context/AppContainer';
import {
  MobileOpenClassSessionParticipant,
  OpenClassSessionParticipantState,
  OpenClassSessionParticipantUpdate,
} from '../types/OpenClassSession';
import { countUnrecordedAttendance } from '../utils/openClassSessionParticipants';

type OpenClassSessionParticipantsParams = {
  sessionId: string;
};

const OpenClassSessionParticipants: React.FC = () => {
  const { sessionId: sessionIdParam } =
    useParams<OpenClassSessionParticipantsParams>();
  const sessionId = Number(sessionIdParam);
  const { t } = useTranslation();
  const [, setAppBarInterface] = useAppBar();
  const [, setCurrentClasses] = useAppContainer();
  const queryClient = useQueryClient();
  const [loadingRegistrationIds, setLoadingRegistrationIds] = useState<
    number[]
  >([]);
  const [unauthorized, setUnauthorized] = useState(false);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentParticipant, setCommentParticipant] =
    useState<MobileOpenClassSessionParticipant>();
  const [selectedParticipants, setSelectedParticipants] = useState<
    MobileOpenClassSessionParticipant[]
  >([]);
  const participantsQueryKey = ['openClassSessionParticipants', sessionId];
  const participantsQuery = useQuery({
    queryKey: participantsQueryKey,
    queryFn: () => API.getOpenClassSessionParticipants(sessionId),
    enabled: Number.isInteger(sessionId),
    retry: false,
  });

  const participants = useMemo(
    () => participantsQuery.data?.data ?? [],
    [participantsQuery.data],
  );
  const unrecordedCount = countUnrecordedAttendance(participants);

  useEffect(() => {
    setAppBarInterface({
      title: t('common.attendees'),
      showBackButton: true,
    });
    setCurrentClasses('bg-white');
  }, [i18next.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateParticipantsInCache = (
    updated: MobileOpenClassSessionParticipant[],
  ) => {
    const byRegistration = new Map(
      updated.map((participant) => [participant.registrationId, participant]),
    );
    queryClient.setQueryData<typeof participantsQuery.data>(
      participantsQueryKey,
      (current) =>
        current
          ? {
              ...current,
              data: current.data.map(
                (participant) =>
                  byRegistration.get(participant.registrationId) ?? participant,
              ),
            }
          : current,
    );
  };

  const handleMutationError = (error: unknown) => {
    if (isAxiosError(error) && error.response?.status === 403) {
      setUnauthorized(true);
      message.error(t('views.OpenClassSessionParticipants.unauthorized'));
      return;
    }
    if (isAxiosError(error) && error.response?.status === 409) {
      void participantsQuery.refetch();
      message.error(
        t(
          error.response.data?.code === 'absentParticipant'
            ? 'views.OpenClassSessionParticipants.absentCannotPass'
            : 'views.OpenClassSessionParticipants.conflict',
        ),
      );
      return;
    }
    message.error(t('errors.somethingWentWrong'));
  };

  const participantMutation = useMutation({
    mutationFn: (updates: OpenClassSessionParticipantUpdate[]) =>
      API.updateOpenClassSessionParticipants(sessionId, updates),
    onSuccess: ({ data }) => updateParticipantsInCache(data),
    onError: handleMutationError,
    onSettled: () => setLoadingRegistrationIds([]),
  });

  const writeStates = (updates: OpenClassSessionParticipantUpdate[]) => {
    setLoadingRegistrationIds(
      updates.map(({ registrationId }) => registrationId),
    );
    participantMutation.mutate(updates);
  };

  const withIdCheck = (markPresent: () => void) => {
    if (
      participants.some(
        ({ attendanceStatus }) => attendanceStatus !== 'not_recorded',
      )
    ) {
      markPresent();
      return;
    }
    Modal.confirm({
      title: t('views.OpenClassSessionParticipants.idCheckTitle'),
      content: t('views.OpenClassSessionParticipants.idCheckDescription'),
      okText: t('components.OpenClassSessionParticipantTable.present'),
      cancelText: t('common.cancel'),
      onOk: markPresent,
    });
  };

  const applyStates = (updates: OpenClassSessionParticipantUpdate[]) =>
    updates.some(({ attendanceStatus }) => attendanceStatus === 'present')
      ? withIdCheck(() => writeStates(updates))
      : writeStates(updates);

  const updateState = (
    participant: MobileOpenClassSessionParticipant,
    state: OpenClassSessionParticipantState,
  ) => applyStates([{ registrationId: participant.registrationId, ...state }]);

  const applyToSelection = (
    state: (
      participant: MobileOpenClassSessionParticipant,
    ) => OpenClassSessionParticipantState,
  ) => {
    const selected = selectedParticipants;
    setSelectedParticipants([]);
    applyStates(
      selected.map((participant) => ({
        registrationId: participant.registrationId,
        ...state(participant),
      })),
    );
  };

  const batchMenuItems = [
    { key: 'present', state: participantStates.present },
    { key: 'not_present', state: participantStates.notPresent },
    { key: 'passed', state: participantStates.passed },
    { key: 'failed', state: participantStates.failed },
  ].map(({ key, state }) => ({
    key,
    label: (
      <div
        className="flex gap-2 items-center"
        onClick={() => applyToSelection(state)}>
        {t(`components.OpenClassSessionParticipantTable.${key}`)}
      </div>
    ),
  }));

  const saveComment = () => {
    if (!commentParticipant) {
      return;
    }
    writeStates([
      {
        registrationId: commentParticipant.registrationId,
        attendanceStatus: commentParticipant.attendanceStatus,
        certificateResultStatus: commentParticipant.certificateResultStatus,
        comment: commentDraft.trim() || null,
      },
    ]);
    setCommentParticipant(undefined);
  };

  if (participantsQuery.isLoading) {
    return (
      <div className="flex justify-center items-center h-5/6">
        <SafeLifeSpin size="large" />
      </div>
    );
  }

  if (unauthorized || participantsQuery.isError) {
    const forbidden =
      unauthorized ||
      (isAxiosError(participantsQuery.error) &&
        participantsQuery.error.response?.status === 403);

    return (
      <div className="p-4 text-center">
        {t(
          forbidden
            ? 'views.OpenClassSessionParticipants.unauthorized'
            : 'views.OpenClassSessionParticipants.unavailable',
        )}
      </div>
    );
  }

  return (
    <div className="bg-white h-full view-max-width max-w-sm">
      <div className="py-6 px-4 flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <div className="text-2xl font-semibold">{t('common.attendees')}</div>
          <Button
            aria-label={t('views.OpenClassSessionParticipants.addParticipant')}
            disabled
            icon={<UserAddOutlined />}
            type="primary"
          />
        </div>
        <div className="flex items-center gap-1.5 text-xs font-medium text-safeLifeMedium">
          <IdcardOutlined />
          {t('views.OpenClassSessionParticipants.idCheckReminder')}
        </div>
        {unrecordedCount > 0 ? (
          <div
            className="rounded-lg p-3 text-sm bg-warningBG text-grayDark"
            role="status">
            {t('views.OpenClassSessionParticipants.review', {
              count: unrecordedCount,
            })}
          </div>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-grayDark">
            <CheckCircleOutlined />
            {t('views.OpenClassSessionParticipants.reviewComplete')}
          </div>
        )}
      </div>
      <OpenClassSessionParticipantTable
        loadingRegistrationIds={loadingRegistrationIds}
        onStateUpdate={updateState}
        onCommentEdit={(participant) => {
          setCommentParticipant(participant);
          setCommentDraft(participant.attendanceComment ?? '');
        }}
        onSelectionChange={setSelectedParticipants}
        participants={participants}
        selectedRegistrationIds={selectedParticipants.map(
          ({ registrationId }) => registrationId,
        )}
      />
      {selectedParticipants.length > 0 && (
        <div
          className="fixed left-0 bottom-0 w-full flex justify-center p-4 gap-2 items-center"
          style={{
            backgroundColor: colors.safeLifeDarker,
            color: colors.white,
          }}>
          <div
            className="px-2 rounded-sm"
            style={{
              backgroundColor: colors.white,
              color: colors.textDefault,
            }}>
            {selectedParticipants.length}
          </div>
          {t('views.CourseInstanceAttendees.attendeesSelected', {
            count: selectedParticipants.length,
          })}
          <Dropdown
            menu={{ items: batchMenuItems }}
            placement="topRight"
            trigger={['click']}>
            <Button icon={<DownOutlined />}>
              {t('views.CourseInstanceAttendees.changeStatus')}
            </Button>
          </Dropdown>
        </div>
      )}
      <Modal
        cancelText={t('common.cancel')}
        okText={t('common.save')}
        onCancel={() => setCommentParticipant(undefined)}
        onOk={saveComment}
        open={commentParticipant !== undefined}
        title={t('components.OpenClassSessionParticipantTable.comment')}>
        <Input.TextArea
          aria-label={t('components.OpenClassSessionParticipantTable.comment')}
          onChange={(event) => setCommentDraft(event.target.value)}
          rows={4}
          value={commentDraft}
        />
      </Modal>
    </div>
  );
};

export default OpenClassSessionParticipants;
